import { AlertTriangle, X } from "lucide-react";
import { describeStep, formatTotalDuration, sumStepDurations } from "utils/stressProfileFormat";
import { describeWaterEvent } from "utils/waterEvents";

/**
 * Review-before-start modal, following CalibrationEditorModal's shell
 * pattern. GreenhouseRunForm's `submitStart` already validates the run
 * before opening this - the modal is a review step, not a second
 * validation pass, so it trusts whatever payload it's given.
 *
 * Props:
 *   isOpen     - Boolean controlling visibility
 *   onClose    - Callback to close the modal ("Cancel" - starts nothing)
 *   payload    - { runId, label, linkedPadihatRunId, steps, sampleMs,
 *                  distanceSamples, waterEvents, waterStartLevelCm, tankMinCm,
 *                  tankMaxCm } (validated) - sampleMs/distanceSamples are null
 *                  when no water device is linked; the tank fields are null
 *                  when there are no water events
 *   pumpConfig - the backend's pump_config (for the pumping-time estimates)
 *   onConfirm  - async () => void - actually calls onStart with `payload`
 *   submitting - Boolean; shows spinner + disables the confirm button
 */
export default function StartRunConfirmModal({ isOpen, onClose, payload, onConfirm, submitting, pumpConfig }) {
  if (!isOpen || !payload) return null;

  const {
    runId,
    label,
    linkedPadihatRunId,
    steps = [],
    sampleMs,
    distanceSamples,
    waterEvents = [],
    waterStartLevelCm,
    tankMinCm,
    tankMaxCm,
  } = payload;
  const totalSeconds = sumStepDurations(steps);

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-end sm:items-center justify-center p-0 sm:p-4">
      <div
        className="bg-white w-full sm:max-w-2xl rounded-t-2xl sm:rounded-xl shadow-lg max-h-[90vh] overflow-y-auto"
        role="dialog"
        aria-modal="true"
        aria-labelledby="start-run-confirm-title"
      >
        <div className="flex items-center justify-between border-b p-6">
          <h3
            id="start-run-confirm-title"
            className="text-xl font-semibold text-gray-900 flex items-center gap-2"
          >
            <AlertTriangle className="w-6 h-6 text-amber-500" />
            Confirm run start
          </h3>
          <button
            onClick={onClose}
            aria-label="Close modal"
            className="text-gray-400 hover:text-gray-600"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        <div className="p-6 space-y-6">
          <dl className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div>
              <dt className="text-xs font-bold uppercase tracking-wide text-slate-500">Run ID</dt>
              <dd className="mt-1 font-mono text-sm text-slate-900">{runId}</dd>
            </div>
            {label && (
              <div>
                <dt className="text-xs font-bold uppercase tracking-wide text-slate-500">Label</dt>
                <dd className="mt-1 text-sm text-slate-900">{label}</dd>
              </div>
            )}
            {linkedPadihatRunId && (
              <div>
                <dt className="text-xs font-bold uppercase tracking-wide text-slate-500">
                  Linked PadiHAT run
                </dt>
                <dd className="mt-1 font-mono text-sm text-slate-900">{linkedPadihatRunId}</dd>
              </div>
            )}
            <div>
              <dt className="text-xs font-bold uppercase tracking-wide text-slate-500">
                Estimated total duration
              </dt>
              <dd className="mt-1 text-sm text-slate-900">{formatTotalDuration(totalSeconds)}</dd>
            </div>
            {sampleMs != null && distanceSamples != null && (
              <div>
                <dt className="text-xs font-bold uppercase tracking-wide text-slate-500">
                  Water sampling
                </dt>
                <dd className="mt-1 text-sm text-slate-900">
                  {sampleMs / 1000}s interval, {distanceSamples} samples/reading
                </dd>
              </div>
            )}
          </dl>

          <div>
            <p className="text-xs font-bold uppercase tracking-wide text-slate-500">
              Steps ({steps.length})
            </p>
            <ol className="mt-2 list-decimal space-y-1 pl-5 text-sm text-slate-800">
              {steps.map((step, index) => (
                <li key={index}>{describeStep(step)}</li>
              ))}
            </ol>
          </div>

          {waterEvents.length > 0 && (
            <div data-testid="confirm-water-events">
              <p className="text-xs font-bold uppercase tracking-wide text-slate-500">
                Water events ({waterEvents.length})
              </p>
              <ol className="mt-2 list-decimal space-y-1 pl-5 text-sm text-slate-800">
                {waterEvents.map((event, index) => (
                  <li key={index}>{describeWaterEvent(event, pumpConfig || undefined)}</li>
                ))}
              </ol>
              <p className="mt-2 text-xs text-slate-600">
                Tank starts at {waterStartLevelCm} cm; allowed range {tankMinCm} to {tankMaxCm} cm. Each
                operation is photographed before and after; if the camera does not confirm the
                before photo, that operation is skipped and the pump does not run.
              </p>
            </div>
          )}

          <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
            <p className="text-sm text-amber-800">
              Sending this will command the controller to start driving these steps in order.
              Double-check the run ID and steps before confirming.
            </p>
          </div>
        </div>

        <div className="flex gap-3 border-t p-6">
          <button
            onClick={onClose}
            className="flex-1 px-4 py-2 text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50"
          >
            Cancel
          </button>
          <button
            onClick={onConfirm}
            disabled={submitting}
            className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
          >
            {submitting ? "Starting…" : "Confirm & start"}
          </button>
        </div>
      </div>
    </div>
  );
}
